import { z } from 'zod'

import { requireDataClassName } from '../shared/entity-serializer'
import { getHostRuntime } from '@/Atomic/Widgets/AiChatBot/core/engine/host-runtime'
import { defineTool } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-system'
import type {
  EntityClassName,
  SelectOption,
  SessionContext,
  ShowOptionsResult,
} from '@/Atomic/Widgets/AiChatBot/core/engine/types'

// Industry-standard select-widget shape. Plain strings are accepted from older
// callers (section pickers like "Activities, Tickets, Deal pipeline") and
// normalised to { value: x, label: x } — no entity metadata, no slot write.
const richOptionSchema = z.object({
  value: z
    .string()
    .describe(
      'Unique identifier for this option. For entities use the record _id; for plain choices use the label itself.',
    ),
  label: z.string().describe('Display text shown to the user.'),
  dataClass: z
    .string()
    .optional()
    .describe(
      'Entity type (Company, Person, Activity, ...). Required for entity selection so the slot system can record the choice.',
    ),
  entityId: z
    .string()
    .optional()
    .describe(
      'Entity _id. Usually equal to value for entity selection, but kept explicit so plain-string options stay clean.',
    ),
  url: z
    .string()
    .optional()
    .describe(
      'Entity _url, sourced from the search/list result. Lets the slot system record a navigable URL.',
    ),
})

const optionSchema = z.union([z.string(), richOptionSchema])

function isShowOptionsResult(v: unknown): v is ShowOptionsResult {
  if (v === null || typeof v !== 'object') return false
  const obj = v as Record<string, unknown>
  return 'cancelled' in obj && typeof obj.cancelled === 'boolean'
}

function asDataClassName(input: string | undefined): EntityClassName | null {
  if (!input) return null
  return requireDataClassName(input)
}

function normaliseOption(
  o: string | z.infer<typeof richOptionSchema>,
): SelectOption {
  if (typeof o === 'string') return { value: o, label: o }
  const dc = asDataClassName(o.dataClass)
  return {
    value: o.value,
    label: o.label,
    ...(dc ? { dataClass: dc } : {}),
    ...(o.entityId ? { entityId: o.entityId } : {}),
    ...(o.url ? { url: o.url } : {}),
  }
}

export const showOptionsTool = defineTool({
  name: 'show_options',
  description:
    'Present options to the user and wait for their response. Supports two variants: (1) "options" for choosing from a list, (2) "dateRange" for selecting a start and end date. ' +
    "For entity selection (e.g. confirming which Company the user meant after search_entities), pass each option as { value, label, dataClass, entityId, url } sourced from the tool result. The system will record the user's choice as a confirmed entity slot, available to subsequent tools without re-searching. " +
    'For plain choices (e.g. "Activities", "Tickets", "Deal pipeline"), pass each option as a plain string. ' +
    'Use variant "dateRange" when you need a start/end date period.',
  inputSchema: () =>
    z.object({
      variant: z
        .enum(['options', 'dateRange'])
        .default('options')
        .describe(
          'Use "options" to present a list of choices, "dateRange" to collect a start and end date',
        ),
      question: z.string().describe('The question to show the user'),
      options: z
        .array(optionSchema)
        .optional()
        .describe(
          'Required when variant is "options". List of choices. Use plain strings for simple section/category pickers, or { value, label, dataClass, entityId, url } for entity selection.',
        ),
      multiSelect: z
        .boolean()
        .optional()
        .describe(
          'When true, user can select multiple options (default false). Only used when variant is "options"',
        ),
      preselected: z
        .array(z.string())
        .optional()
        .describe(
          'Option values to pre-select. Each value must match an option.value. The user can change the selection before submitting. Only used when variant is "options".',
        ),
      allowEmpty: z
        .boolean()
        .optional()
        .describe(
          'When true, the user may submit with nothing selected (an empty selection is a valid answer, not a cancel). Use ONLY for opt-in refinement pickers such as the summarize section chooser. Leave unset for any picker where a choice is required. Only used when variant is "options".',
        ),
      defaultStartDate: z
        .string()
        .optional()
        .describe(
          'Default start date in YYYY-MM-DD format. Only used when variant is "dateRange"',
        ),
      defaultEndDate: z
        .string()
        .optional()
        .describe(
          'Default end date in YYYY-MM-DD format. Only used when variant is "dateRange"',
        ),
    }),
  concurrencySafe: false,
  cacheable: false,

  async run(args, ctx: SessionContext, toolCallId?: string) {
    const id = toolCallId ?? '_unknown'

    if (args.variant === 'dateRange') {
      const { start: urlStart, end: urlEnd } =
        getHostRuntime().getUrlDateRange?.(ctx.pageContext?.urlQuery) ?? {
          start: undefined,
          end: undefined,
        }
      return new Promise<ShowOptionsResult>((resolve, reject) => {
        ctx.registerToolCallResolver?.(
          id,
          {
            type: 'dateRange',
            toolCallId: id,
            question: args.question,
            defaultStartDate: args.defaultStartDate ?? urlStart,
            defaultEndDate: args.defaultEndDate ?? urlEnd,
          },
          resolve as (result: unknown) => void,
          reject,
        )
      })
    }

    const normalised = (args.options ?? []).map(normaliseOption)
    const optionValues = new Set(normalised.map((o) => o.value))
    const preselected = (args.preselected ?? []).filter((v) =>
      optionValues.has(v),
    )
    return new Promise<ShowOptionsResult>((resolve, reject) => {
      ctx.registerToolCallResolver?.(
        id,
        {
          type: 'options',
          toolCallId: id,
          question: args.question,
          options: normalised,
          multiSelect: args.multiSelect,
          preselected: preselected.length > 0 ? preselected : undefined,
          allowEmpty: args.allowEmpty,
        },
        (result: unknown) => {
          // Slot-write side effect: when the user resolves a single entity
          // option (dataClass + entityId + url all present), record it via
          // SessionContext.addConfirmedEntity. Section pickers carry no entity
          // metadata → no-op. Multi-select → no-op (ambiguous which to record).
          if (!isShowOptionsResult(result)) {
            resolve(result as ShowOptionsResult)
            return
          }
          if (
            'selected' in result &&
            !result.cancelled &&
            result.selected.length === 1 &&
            !args.multiSelect
          ) {
            const chosen = normalised.find(
              (o) => o.value === result.selected[0],
            )
            if (
              chosen?.dataClass &&
              chosen.entityId &&
              chosen.url &&
              ctx.addConfirmedEntity
            ) {
              ctx.addConfirmedEntity({
                dataClass: chosen.dataClass,
                id: chosen.entityId,
                title: chosen.label,
                url: chosen.url,
              })
            }
          }
          resolve(result)
        },
        reject,
      )
    })
  },
})
